<template>
  <!-- SAFETY NET: This v-if ensures the component never renders until the data is perfectly ready -->
  <div class="quantification-view" v-if="kvdr && kvdr.quantification">
    <p class="subtitle-text">Convert business impact into {{ currentCurrencySymbol }} so we can reason about value/cost ratios.</p>
    
    <!-- CURRENCY TABS -->
    <div class="currency-tabs">
        <span class="c-label">Currency</span>
        <button v-for="c in currencies" :key="c.id" :class="{active: kvdr.quantification.currency === c.id}" @click="kvdr.quantification.currency = c.id">
            {{ c.symbol }} {{ c.id }}
        </button>
    </div>

    <!-- WHITE METRIC CARDS -->
    <div class="grid-2">
  <div class="metric-card">
    <label>
      <span class="icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="3 17 9 11 13 15 21 7"></polyline>
          <polyline points="14 7 21 7 21 14"></polyline>
        </svg>
      </span>
      Annual revenue influenced
    </label>
    <div class="input-wrap">
      <span class="sym">{{ currentCurrencySymbol }}</span>
      <input type="number" v-model.number="kvdr.quantification.revenueInfluenced">
    </div>
    <p class="hint">New or retained revenue this role drives.</p>
  </div>

  <div class="metric-card">
    <label>
      <span class="icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="9"></circle>
          <path d="M16 8h-5a2 2 0 0 0 0 4h2a2 2 0 0 1 0 4H8"></path>
          <line x1="12" y1="6" x2="12" y2="18"></line>
        </svg>
      </span>
      Cost saved annually
    </label>
    <div class="input-wrap">
      <span class="sym">{{ currentCurrencySymbol }}</span>
      <input type="number" v-model.number="kvdr.quantification.costSaved">
    </div>
    <p class="hint">Recurring savings (process, vendor, headcount avoided).</p>
  </div>

  <div class="metric-card">
    <label>
      <span class="icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M12 3L4 7v5c0 5 3.5 8 8 9 4.5-1 8-4 8-9V7l-8-4z"></path>
          <path d="M9 12l2 2 4-4"></path>
        </svg>
      </span>
      Risk reduced (exposure avoided)
    </label>
    <div class="input-wrap">
      <span class="sym">{{ currentCurrencySymbol }}</span>
      <input type="number" v-model.number="kvdr.quantification.riskReduced">
    </div>
    <p class="hint">Estimated annualized loss prevented.</p>
  </div>

  <div class="metric-card">
    <label>
      <span class="icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="9"></circle>
          <polyline points="12 7 12 12 15 14"></polyline>
        </svg>
      </span>
      Cycle time reduced (value)
    </label>
    <div class="input-wrap">
      <span class="sym">{{ currentCurrencySymbol }}</span>
      <input type="number" v-model.number="kvdr.quantification.cycleTimeValue">
    </div>
    <p class="hint">Hours/week saved × loaded hourly rate × 50.</p>
  </div>

  <div class="metric-card">
    <label>
      <span class="icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8L12 3z"></path>
          <path d="M19 16l.7 2.3L22 19l-2.3.7L19 22l-.7-2.3L16 19l2.3-.7L19 16z"></path>
        </svg>
      </span>
      Quality delta (value)
    </label>
    <div class="input-wrap">
      <span class="sym">{{ currentCurrencySymbol }}</span>
      <input type="number" v-model.number="kvdr.quantification.qualityValue">
    </div>
    <p class="hint">Defect reduction, NPS lift, churn avoided.</p>
  </div>

  <div class="metric-card">
    <label>
      <span class="icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M9 18h6"></path>
          <path d="M10 22h4"></path>
          <path d="M8.5 14.5A6 6 0 1 1 15.5 14.5c-.8.6-1.2 1.4-1.3 2.5h-4.4c-.1-1.1-.5-1.9-1.3-2.5z"></path>
        </svg>
      </span>
      Strategic option value
    </label>
    <div class="input-wrap">
      <span class="sym">{{ currentCurrencySymbol }}</span>
      <input type="number" v-model.number="kvdr.quantification.strategicValue">
    </div>
    <p class="hint">Optionality / capability built (long-term).</p>
  </div>
</div>

    <!-- WIDER CARDS FOR BOTTOM ELEMENTS -->
    <div class="metric-card single-card">
      <label>Indicative value events / notes</label>
      <textarea v-model="kvdr.quantification.notes" class="text-input" placeholder="e.g. Closed €2M deal in Q3..."></textarea>
    </div>

    <div class="metric-card single-card">
      <label>ATTRIBUTION CONFIDENCE <span class="score-badge">{{ kvdr.quantification.confidence }}%</span></label>
      
      <!-- REACTIVE SLIDER FIX -->
      <input type="range" min="0" max="100" v-model.number="kvdr.quantification.confidence" class="slider" :style="{ background: `linear-gradient(to right, #F59E0B 0%, #F59E0B ${kvdr.quantification.confidence}%, #e2e8f0 ${kvdr.quantification.confidence}%, #e2e8f0 100%)` }">
      
      <p class="hint mt-8">How confident are you that this value is attributable to this role specifically? (Auto-suggested from Attribution layer.)</p>
    </div>
    
    <div class="metric-card single-card pb-24">
      <div class="flex-between">
        <label>VALUE BAND THRESHOLDS</label>
        <span class="hint">Drag to set Low / Mid / High cutoffs</span>
      </div>
      <div class="grid-2 mt-8">
        <div>
          <span class="threshold-label">Low / Mid cutoff</span>
          <div class="input-wrap"><span class="sym">{{ currentCurrencySymbol }}</span><input type="number" v-model.number="kvdr.quantification.lowMidThreshold"></div>
        </div>
        <div>
          <span class="threshold-label">Mid / High cutoff</span>
          <div class="input-wrap"><span class="sym">{{ currentCurrencySymbol }}</span><input type="number" v-model.number="kvdr.quantification.midHighThreshold"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'QuantificationLayer',
  // CRITICAL: These props must be here for Vue to know what 'kvdr' is.
  props: {
    kvdr: { type: Object, required: true }, 
    currencies: { type: Array, required: true }, 
    currentCurrencySymbol: { type: String, required: true },
    formatNumber: { type: Function, required: true }, 
    adjustedValue: { type: Number, required: true }, 
    rawValue: { type: Number, required: true }, 
    valueBand: { type: String, required: true }
  }
}
</script>

<style scoped>
.quantification-view { display: flex; flex-direction: column; gap: 12px; } /* Natural gap handles spacing */
.subtitle-text { font-size: 13px; color: #64748b; margin: 0 0 4px 0; }
.currency-tabs { display: flex; align-items: center; gap: 8px; margin-bottom: 4px; }
.c-label { font-size: 12px; font-weight: 600; color: #64748b; margin-right: 8px; }
.currency-tabs button { padding: 6px 14px; border-radius: 16px; border: 1px solid #cbd5e1; background: #fff; font-size: 12px; color: #475569; cursor: pointer; transition: all 0.2s; font-weight: 500; }
.currency-tabs button.active { background: #Fef3c7; border-color: #F59E0B; color: #d97706; font-weight: 700; }

.grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; } 
.mt-8 { margin-top: 8px; }
.pb-24 { margin-bottom: 16px; }

.metric-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 16px; display: flex; flex-direction: column; gap: 10px; transition: box-shadow 0.2s; }
.metric-card:focus-within { border-color: #F59E0B; box-shadow: 0 4px 12px rgba(245, 158, 11, 0.1); }
.metric-card label { font-size: 13px; font-weight: 700; color: #334155; display: flex; align-items: center; gap: 8px; margin: 0; }
.icon { font-size: 16px; }
.hint { font-size: 12px; color: #94a3b8; margin: 0; line-height: 1.4; }

.input-wrap { position: relative; display: flex; align-items: center; }
.sym { position: absolute; left: 16px; color: #64748b; font-weight: 600; font-size: 14px; pointer-events: none; }
.input-wrap input { width: 100%; padding: 10px 16px 10px 36px; border: 1px solid #cbd5e1; border-radius: 8px; font-size: 14px; outline: none; transition: 0.2s; } 
.input-wrap input:focus { border-color: #F59E0B; }

.text-input { width: 100%; padding: 12px; border: 1px solid #cbd5e1; border-radius: 8px; font-size: 14px; height: 60px; resize: vertical; outline: none; font-family: inherit; } 
.text-input:focus { border-color: #F59E0B; }

.flex-between { display: flex; justify-content: space-between; align-items: flex-end; }
.threshold-label { display: block; font-size: 11px; font-weight: 600; color: #64748b; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.5px; }

.slider { width: 100%; appearance: none; height: 6px; border-radius: 4px; outline: none; background: #e2e8f0; margin: 6px 0; }
.slider::-webkit-slider-thumb { appearance: none; width: 16px; height: 16px; border-radius: 50%; background: #F59E0B; cursor: pointer; border: 2px solid #fff; box-shadow: 0 1px 3px rgba(0,0,0,0.3); }
.score-badge { background: #f1f5f9; padding: 2px 8px; border-radius: 10px; color: #0f172a; font-weight: 700; margin-left: auto;}
.metric-card .icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    margin-right: 6px;
    vertical-align: middle;
}

.metric-card .icon svg {
    width: 16px;
    height: 16px;
    display: block;
    color: currentColor;
    stroke: currentColor;
}
</style>